import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import {
  ESocialCheckStatus,
  EWebhookEvent,
  EReportType,
} from '@/store/api/Enterprise/Enterprise.types'
import type { ReportCheckData } from '@/store/api/Workspace/Workspace.types'

export type ReportStatusVariant =
  | 'generating'
  | 'in-progress'
  | 'clean'
  | 'neutral'
  | 'risk'
  | 'pending-consent'
  | 'cancelled'

interface ReportStatusPillProps {
  variant: ReportStatusVariant
  className?: string
}

const Spinner = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="animate-spin"
    aria-hidden="true"
  >
    <path
      d="M12 3a9 9 0 1 0 9 9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
)

const variantClasses: Record<ReportStatusVariant, string> = {
  generating:
    'bg-baseColorHighlight border border-neutralColor5 text-neutralColor',
  'in-progress': 'bg-dynamicBlue border border-dynamicBlue text-staticWhite',
  clean: 'bg-baseColorHighlight border border-neutralColor5 text-neutralColor',
  neutral:
    'bg-baseColorHighlight border border-neutralColor5 text-neutralColor',
  risk: 'bg-dynamicRed text-staticWhite',
  'pending-consent':
    'bg-dynamicBlue border border-dynamicBlue text-staticWhite',
  cancelled: 'bg-dynamicRed text-staticWhite',
}

const variantLabelKeys: Record<ReportStatusVariant, string> = {
  generating: 'dashboard.reportsScreen.generating',
  'in-progress': 'dashboard.reportsScreen.inProgress',
  clean: 'dashboard.reportsScreen.clean',
  neutral: 'dashboard.reportsScreen.neutral',
  risk: 'dashboard.reportsScreen.risk',
  'pending-consent': 'dashboard.reportsScreen.concentPending',
  cancelled: 'dashboard.reportsScreen.cancelled',
}

export const ReportStatusPill = ({
  variant,
  className,
}: ReportStatusPillProps) => {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium leading-4 whitespace-nowrap',
        variantClasses[variant],
        className,
      )}
    >
      {t(variantLabelKeys[variant])}
      {variant === 'generating' && <Spinner />}
    </span>
  )
}

export const resolveSocialCheckPillVariant = (
  data: ReportCheckData,
): ReportStatusVariant | null => {
  const status = data.status as ESocialCheckStatus

  if (status === ESocialCheckStatus.Cancelled) return 'cancelled'

  if (
    status === ESocialCheckStatus.Error ||
    status === ESocialCheckStatus.ErroPdf
  ) {
    return null
  }

  if (
    status === ESocialCheckStatus.NotReleased &&
    data.complianceStatus !== EWebhookEvent.COMPLETED
  ) {
    return 'pending-consent'
  }

  if (status === ESocialCheckStatus.Complete) {
    if (data.contentRadar === 'low') return 'clean'
    if (data.contentRadar === 'medium') return 'neutral'
    if (data.contentRadar === 'high') return 'risk'
    return null
  }

  return data.reportType === EReportType.Ai ? 'generating' : 'in-progress'
}
